import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import Logo from "@/components/Logo";
import { CTA, ConversionCTA, prefersReducedMotion } from "@/components/system";

/* "/#section" form so the anchors resolve from subpages too (plain fragment
   scroll on the homepage, full navigation back home from anywhere else).

   Two entries used to be four. "Approach" pointed at the same `/#services` as
   "Services", so two items lit up at once AND two live nodes shared the
   `layoutId="nav-underline"` — which framer's shared layout cannot animate
   between, hence the console warning. The warning was a symptom of duplicated
   data, not a motion bug.

   "How It Works" pointed at `/#engagement`, which on the homepage is a nested
   <div> inside `#services`, not a section. A spy target inside another spy
   target has no defined winner, and the ladder it linked to is already the
   second half of Services — so the link was removed rather than pointed
   somewhere it does not exist. */
const navLinks = [
  { label: "Work",     href: "/#showcase" },
  { label: "Services", href: "/#services" },
];

const solutionLinks = [
  { label: "Micro Drama Production", href: "/micro-drama-production" },
  { label: "For Story Apps & OTT",   href: "/solutions/platforms" },
  { label: "For Education",          href: "/solutions/education" },
  { label: "Growth for Brands",      href: "/solutions/growth" },
];

const MICRO_DRAMA_PATH = "/micro-drama-production";

// The header is fixed at h-20. A section becomes "current" once its top has
// travelled under that 80px of chrome, plus a little margin so the switch
// happens as the heading meets the bar rather than a beat after it.
const HEADER_H = 80;
const SPY_OFFSET = 24;

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("");
  const { pathname } = useLocation();

  useEffect(() => {
    if (!isMobileMenuOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setMobileMenuOpen(false); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [isMobileMenuOpen]);

  /* ── Scroll spy ───────────────────────────────────────────────────────────
     This replaces an IntersectionObserver per nav id that had four faults at
     once: it set a current section and never cleared it (so there was no valid
     top-of-page state, and it latched onto whatever it saw last once you
     scrolled back up); `threshold: 0.35` measured a fraction of the ELEMENT, so
     a section taller than ~2.85 viewports could never activate while a short
     nested one trivially did; it allowed nothing for the 80px fixed header; and
     it resolved its elements once on mount, so the lazy routes — which mount
     their content after the nav — cached `null` and never recovered.

     An ordered resolver has none of those failure modes: read the real
     positions, pick the last section whose top has passed the header, and let
     "above the first section" be an ordinary answer. */
  useEffect(() => {
    let frame = 0;

    const read = () => {
      frame = 0;
      const scrollY = window.scrollY;
      setIsScrolled(scrollY > 40);

      // Resolved every pass rather than cached: lazy routes swap their whole
      // tree in under us. Sorted by position because nav order is an editorial
      // decision and does not have to match document order.
      const targets = navLinks
        .map((link) => {
          const el = document.getElementById(link.href.split("#")[1]);
          if (!el) return null;
          return { href: link.href, top: el.getBoundingClientRect().top + scrollY };
        })
        .filter(Boolean)
        .sort((a, b) => a.top - b.top);

      const marker = scrollY + HEADER_H + SPY_OFFSET;
      // "" is a real state, not a missing one: at the top of the page you are
      // in no section yet. The old observer could not express that, which is
      // why "How It Works" sat underlined above the hero.
      let current = "";
      for (const target of targets) {
        if (target.top <= marker) current = target.href;
      }
      setActiveSection((prev) => (prev === current ? prev : current));
    };

    // Coalesced to one read per frame — the handler measures geometry, and an
    // unthrottled scroll fires far more often than the page can paint.
    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(read);
    };

    read();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [pathname]);

  // Nav is wayfinding, so it is set as prose: the working register at body size
  // in sentence case. It used to be 11px mono caps at five different trackings,
  // which made the one thing on the page that must be scannable the hardest
  // thing on it to read.
  const navItemClass = "relative flex min-h-11 items-center pb-1 font-body text-body transition-colors duration-hover ease-gate";

  return (
    <>
      {/* No entrance animation: the only navigation on the page used to start
          64px off-screen, which delays the one control a visitor may want
          before anything has finished loading. */}
      <header
        className={`fixed inset-x-0 top-0 z-[100] transition-colors duration-hover ease-gate ${
          isScrolled
            ? "border-b border-line bg-surface/95 backdrop-blur-xl"
            : "border-b border-transparent"
        }`}
        data-testid="navbar"
      >
        <nav
          aria-label="Primary"
          className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6 md:px-10"
        >
          <a
            href="/"
            data-testid="navbar-logo"
            aria-label="Kyrosity, home"
            className="flex min-h-11 items-center"
          >
            <Logo />
          </a>

          {/* Desktop nav */}
          <div className="hidden items-center gap-8 lg:flex">
            {navLinks.map((link) => {
              const active = activeSection === link.href;
              return (
                <a
                  key={link.label}
                  href={link.href}
                  // "true" is not a value aria-current accepts for this job:
                  // the section is where you ARE within the page, not the page.
                  aria-current={active ? "location" : undefined}
                  data-testid={`nav-${link.label.toLowerCase()}`}
                  className={`${navItemClass} ${active ? "text-bone" : "text-mute hover:text-bone"}`}
                >
                  {link.label}
                  {active && (
                    <motion.span
                      layoutId="nav-underline"
                      aria-hidden="true"
                      // Neutral, not gold. The underline reports where you are;
                      // gold on this site promises "click me", and it is already
                      // spoken for by the CTA 100px to the right of it.
                      className="absolute inset-x-0 bottom-0 h-px bg-bone"
                      transition={{ type: "spring", stiffness: 380, damping: 30 }}
                    />
                  )}
                </a>
              );
            })}

            {/* Flagship gets a top-level link, not just a dropdown entry */}
            <Link
              to={MICRO_DRAMA_PATH}
              data-testid="nav-micro-drama"
              aria-current={pathname === MICRO_DRAMA_PATH ? "page" : undefined}
              className={`${navItemClass} ${
                pathname === MICRO_DRAMA_PATH ? "text-bone" : "text-mute hover:text-bone"
              }`}
            >
              Micro Drama
            </Link>

            <DropdownMenu>
              <DropdownMenuTrigger
                data-testid="nav-solutions"
                className={`${navItemClass} gap-1.5 text-mute outline-none hover:text-bone data-[state=open]:text-bone`}
              >
                Solutions
                <ChevronDown aria-hidden="true" className="h-4 w-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="min-w-[240px] border-line bg-surface"
              >
                {/* Micro-drama has its own top-level link on desktop */}
                {solutionLinks.filter((l) => l.href !== MICRO_DRAMA_PATH).map((link) => (
                  <DropdownMenuItem key={link.href} asChild>
                    <Link
                      to={link.href}
                      aria-current={pathname === link.href ? "page" : undefined}
                      className="cursor-pointer px-3 py-3 text-mute focus:text-bone"
                    >
                      {/* The vendored radix item carries its own `text-sm`, so
                          the label states the nav's step on its own element
                          rather than racing it in the cascade. */}
                      <span className="font-body text-body">{link.label}</span>
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <ConversionCTA
              size="md"
              arrow={false}
              data-testid="nav-cta"
            />
          </div>

          {/* Mobile toggle */}
          <button
            type="button"
            onClick={() => setMobileMenuOpen(!isMobileMenuOpen)}
            className="flex h-11 w-11 items-center justify-center text-bone lg:hidden"
            data-testid="mobile-menu-toggle"
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-menu"
          >
            {isMobileMenuOpen
              ? <X aria-hidden="true" className="h-6 w-6" />
              : <Menu aria-hidden="true" className="h-6 w-6" />}
          </button>
        </nav>
      </header>

      {/* Mobile menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[90] lg:hidden"
            id="mobile-menu"
          >
            <div
              className="absolute inset-0 bg-ground/95 backdrop-blur-xl"
              onClick={() => setMobileMenuOpen(false)}
            />
            <nav
              aria-label="Mobile"
              className="relative z-10 flex h-full flex-col justify-start gap-2 overflow-y-auto px-6 pb-10 pt-28"
            >
              {/* Sentence case, from the scale. These were mono caps at
                  font-display's 400, i.e. the largest type in the sheet was
                  also the lightest. No hover colour: they already sit at full
                  contrast, and the only gold in the sheet should be the CTA. */}
              {navLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  aria-current={activeSection === link.href ? "location" : undefined}
                  onClick={() => setMobileMenuOpen(false)}
                  className="border-b border-line py-4 font-display text-h2 text-bone"
                >
                  {link.label}
                </a>
              ))}

              {/* A group head, not a section eyebrow — the mono label is
                  reserved for eyebrows and data captions. */}
              <span className="mt-8 font-body text-caption text-faint">
                Solutions
              </span>
              {solutionLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  aria-current={pathname === link.href ? "page" : undefined}
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex min-h-11 items-center font-body text-body text-mute transition-colors duration-hover ease-gate hover:text-bone"
                >
                  {link.label}
                </Link>
              ))}

              {/* Same button, same label, same size as the desktop one — it was
                  the same action rendered two ways by breakpoint. */}
              {/* The panel must close before ConversionCTA scrolls or
                  navigates, or the reader lands on the form behind an open
                  menu. ConversionCTA calls this first, then decides. */}
              <ConversionCTA
                onClick={() => setMobileMenuOpen(false)}
                size="md"
                arrow={false}
                className="mt-8 w-full"
              />
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
